/**
 * DEFUSE S15 — typed property descriptors for the curve kinds
 * (Ellipse / Spline / LWPOLYLINE). Special fields only.
 *
 * Serialized-string rows (Spline control/fit points + knots/weights, LWPOLYLINE
 * Vertices/Bulges) mirror dxfGeometry.buildXxx EXACTLY: `x,y;x,y` for points,
 * comma-joined `formatNumber` for knots/weights/bulges. `read` returns the raw
 * array (S15 write home), `display` serializes it, `toRaw` parses the edited
 * string. Point parsing accepts an OPTIONAL third `z` component (the document
 * parser preserves 3-D control/fit points — P2 #8; the x,y-only serialize is the
 * production wire form, so a display round-trip is lossy on z, which S15
 * reconciles via the live-document point by index). Knots/Weights parsing is
 * all-or-nothing (rejects blank / non-finite tokens — P2 #9).
 */

import type {
  EllipseEntity,
  LwpolylineEntity,
  SplineEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  computePolylineArea,
  computePolylineLength,
  formatNumber,
  parsePointListProperty,
} from "../geometryEntityTypes";
import {
  calc,
  coord,
  make,
  num,
  type DescriptorConstraints,
  type TypedPropertyDescriptor,
} from "./types";

/* ── serialization mirroring the production builders ───────────────────────── */

function serializePoints(pts: readonly { x: number; y: number }[]): string {
  return pts.map((p) => `${formatNumber(p.x)},${formatNumber(p.y)}`).join(";");
}
/** parse `x,y[,z];…` — RELOCATED to `../geometryEntityTypes.ts` beside the
 *  `formatNumber` that serializes the same wire form, so the MVIEW-FIT extent
 *  walk (`extent.ts`) decodes a `Vertices` bag through the SAME parser instead
 *  of growing a second one. Semantics unchanged (z optional, all-or-nothing
 *  reject → S15 no-op); see that function's doc. */
function serializeNumbers(ns: readonly number[]): string {
  return ns.map((n) => formatNumber(n)).join(",");
}
/** all-or-nothing (P2 #9): a blank/non-finite token rejects the WHOLE edit
 *  (returns null → S15 no-op) rather than silently dropping/NaN-corrupting. */
function parseNumbers(s: string): number[] | null {
  if (s.trim() === "") return [];
  const out: number[] = [];
  for (const token of s.split(",")) {
    if (token.trim() === "") return null;
    const n = Number(token);
    if (!Number.isFinite(n)) return null;
    out.push(n);
  }
  return out;
}

/**
 * serialized point-array cell. `toRaw` MERGES the edited x,y (and explicit z)
 * into the EXISTING typed points by index, preserving every non-coordinate
 * per-point field (LWPOLYLINE bulge / startWidth / endWidth / vertexId; Spline's
 * Z when the token is a 2-pair) — mirrors the translator's "zip new positions
 * with the live document vertex attributes by index" (P2 R5 #2). COUNT-LOCKED:
 * a length change (grip/property edits never change the count) rejects → null.
 *
 * `allowZ:false` (LWPOLYLINE Vertices) REJECTS a 3-token `x,y,z` pair outright —
 * an LWPOLYLINE vertex is strictly 2-D (`polylineVertexSchema` has no `z`), and
 * the legacy document translator (`parseCoordinatePairList`, `tokens.length !==
 * 2` reject) no-opped such input; accepting it here would fabricate a stray `z`
 * key the schema strips (S15 Slice A write parity). Splines keep the default
 * `allowZ:true` (a 3-D restore string carries Z verbatim).
 *
 * `countLock:false` (S15 Slice D — SPLINE Control points / Fit points) DROPS the
 * length-change reject. A spline's control-point / fit-point count legitimately
 * CHANGES: a fit-point edit recomputes the control polygon (`fitPointsToControl-
 * Points` emits `numFitPoints + 2` control points), and the UNDO inverse restores
 * the ORIGINAL (different) count — the exact reason the deleted legacy
 * `parseSplinePointList` carried NO count lock. LWPOLYLINE keeps the default
 * `countLock:true` (a vertex grip/property edit preserves the count).
 */
function pointArray<E extends SplineEntity | LwpolylineEntity>(
  label: string,
  path: string,
  read: (e: E) => readonly { x: number; y: number }[],
  opts: { allowZ?: boolean; countLock?: boolean; constraints?: DescriptorConstraints } = {},
): TypedPropertyDescriptor {
  const allowZ = opts.allowZ ?? true;
  const countLock = opts.countLock ?? true;
  return make(label, "text", { path, read: (e) => read(e as E) }, {
    ...(opts.constraints ? { constraints: opts.constraints } : {}),
    display: (e) => serializePoints(read(e as E)),
    toRaw: (s, entity) => {
      const coords = parsePointListProperty(s);
      if (coords === null) return null;
      if (!allowZ && coords.some((c) => c.z !== undefined)) return null;
      const existing = entity ? read(entity as E) : [];
      if (countLock && existing.length > 0 && coords.length !== existing.length) {
        return null;
      }
      return coords.map((c, i) => {
        const src = (existing[i] ?? {}) as Record<string, unknown>;
        const merged: Record<string, unknown> = { ...src, x: c.x, y: c.y };
        // Z resolution — mirrors the DELETED legacy `parsePts`'s
        // `pt.z ?? readRawZ(existing[i]) ?? 0` fallback chain:
        //   1. explicit z in the submitted value wins (3-D restore);
        //   2. else the EXISTING source point's z (Z-preserve on a 2-D edit);
        //   3. else — a GENUINELY-NEW 3-D point (count-GROWING edit past the source
        //      list, countLock:false) — SEED z:0 so a `drawingPoint3DSchema` point
        //      is never persisted WITHOUT its required z. Without this, PROD (which
        //      skips the Zod re-parse in `assembleDrawingDocumentInPlace`, so the
        //      z `.default(0)` never fires) would persist a schema-invalid
        //      `{x,y}` control/fit point → downstream `z: undefined` NaN geometry
        //      (@codex P2 on curves.ts, same class as the Slice-C absent-base P1).
        //   `allowZ:false` (LWPOLYLINE Vertices, strictly 2-D) NEVER seeds z — a
        //   polyline vertex has no z field, and countLock:true forbids growth there.
        if (c.z !== undefined) merged.z = c.z;
        else if ("z" in src) merged.z = src.z; // keep existing Z (spline preserve)
        else if (allowZ) merged.z = 0; // new 3-D point → seed the required z
        return merged;
      });
    },
  });
}
/** serialized number-array cell (all-or-nothing parse). */
function numberArray<E extends SplineEntity>(
  label: string,
  path: string,
  read: (e: E) => readonly number[],
): TypedPropertyDescriptor {
  return make(label, "text", { path, read: (e) => read(e as E) }, {
    display: (e) => serializeNumbers(read(e as E)),
    toRaw: (s) => parseNumbers(s),
  });
}

export const ellipseDescriptors: TypedPropertyDescriptor[] = [
  // S15 Slice B — Ellipse is typed-writer WRITABLE via the extended-geometry
  // cutover (`TYPED_EXTENDED_GEOMETRY_KINDS` + geometryReducer Step 1d); the
  // shell mirrors through `extendedPatchableLookup.Ellipse` +
  // `typedExtendedGeometryShellRejects`. Start/End parameter are RADIANS
  // (shown raw, like the builder); Major axis X/Y is the group-11/21 endpoint
  // VECTOR relative to center; Ratio keeps the AutoCAD (0, 1] bound
  // (descriptor exclusiveMin/max is the gate — tighter than the schema's plain
  // .positive(), which stays untouched). Grip drag is ALSO wired
  // (GRIPS-DRAG-MORE-TYPES, apps/web `gripDragTranslate.ts`'s "Ellipse" case) —
  // center / major-axis / minor-axis grips route through these same
  // descriptors via Center X/Y, Major axis X/Y, Ratio.
  coord<EllipseEntity>("Center X", "center.x", (e) => e.center.x),
  coord<EllipseEntity>("Center Y", "center.y", (e) => e.center.y),
  coord<EllipseEntity>("Major axis X", "majorAxisEndpoint.x", (e) => e.majorAxisEndpoint.x),
  coord<EllipseEntity>("Major axis Y", "majorAxisEndpoint.y", (e) => e.majorAxisEndpoint.y),
  num<EllipseEntity>("Ratio", "ratio", (e) => e.ratio, { exclusiveMin: 0, max: 1 }),
  num<EllipseEntity>("Start parameter", "startParam", (e) => e.startParam),
  num<EllipseEntity>("End parameter", "endParam", (e) => e.endParam),
];

export const splineDescriptors: TypedPropertyDescriptor[] = [
  // S15 Slice D — SPLINE typed-writer cutover (geometryReducer Step 1d for the
  // PLAIN leaves + Step 1d-se for the SIDE-EFFECT `Fit points`). Control points /
  // Knots / Weights / Rational cut over from the deleted legacy branch by-parity.
  //
  // Degree / Closed are READ-ONLY-calc (display-only), NOT writable leaves. THE
  // NURBS WELL-FORMEDNESS INVARIANT `knots.length === controlPoints.length +
  // degree + 1` couples degree to the knot vector: a bare scalar `degree` write
  // with NO knot rebuild inverts the knot domain → the curve VANISHES (empty
  // Bezier set) AND exports an invalid SPLINE (group 71 degree inconsistent with
  // group 72 knot count) the ezdxf strict-readfile oracle REJECTS — a ПРИОРИТЕТ
  // #1 simplification (AutoCAD treats a degree change as a degree-ELEVATION
  // rebuild preserving shape, never a scalar poke). `Closed` is the same class one
  // tier down (render ignores `closed`, but export writes group-70 bit 0 →
  // in-app-vs-export divergence + a closed flag over open clamped knots is
  // self-inconsistent on re-import). The DELETED legacy branch NEVER wrote either
  // (silent no-op), so read-only-calc is the EXACT prior behaviour — no
  // regression. A real degree-elevation / close-spline edit needs a proper NURBS
  // rebuild slice (future). Mirrors the deferred Hatch `Solid` read-only pattern.
  make("Degree", "number", { path: "degree", read: (e) => (e as SplineEntity).degree }, {
    channel: "read-only-calc",
    constraints: { readOnly: true },
    display: (e) => String((e as SplineEntity).degree),
  }),
  make("Closed", "boolean", { path: "closed", read: (e) => (e as SplineEntity).closed }, {
    channel: "read-only-calc",
    constraints: { readOnly: true },
    display: (e) => ((e as SplineEntity).closed ? "Yes" : "No"),
  }),
  // `Rational` matches the DELETED legacy branch's exact truthy set
  // ({yes,true,1} — NOT the `bool` helper's {yes,true,on,1}); "on" parses to
  // false, byte-parity with the legacy `rationalChange` parse (propertyTranslator
  // ~932). Same precedent as the Slice E Hatch `Associative` explicit toRaw.
  make(
    "Rational",
    "boolean",
    { path: "rational", read: (e) => (e as SplineEntity).rational },
    {
      display: (e) => ((e as SplineEntity).rational ? "Yes" : "No"),
      toRaw: (s) => ["yes", "true", "1"].includes(s.trim().toLowerCase()),
    },
  ),
  // Control points — count-UNLOCKED (a fit-recompute / undo changes the count).
  pointArray<SplineEntity>("Control points", "controlPoints", (e) => e.controlPoints, {
    countLock: false,
  }),
  // Fit points — the SIDE-EFFECT leaf: setting it recomputes the control polygon
  // (`splineFitRecomputeMutator`, sideEffectMutators.ts) so the RENDERED curve
  // (built from `controlPoints`, NOT `fitPoints`) actually reshapes (#317). A
  // plain write of `fitPoints` alone would leave the curve on the old control
  // polygon. Count-UNLOCKED so a clear ("" → []) and an undo-restore of a
  // different count both land (legacy `parseSplinePointList` had no count lock).
  pointArray<SplineEntity>("Fit points", "fitPoints", (e) => e.fitPoints, {
    countLock: false,
    constraints: { sideEffects: "splineFitRecomputeMutator" },
  }),
  numberArray<SplineEntity>("Knots", "knots", (e) => e.knots),
  numberArray<SplineEntity>("Weights", "weights", (e) => e.weights),
];

export const lwpolylineDescriptors: TypedPropertyDescriptor[] = [
  // production emits Vertices as the `x,y;x,y` COORDINATE STRING (not a count) —
  // display + toRaw are the consistent coordinate serialize/parse pair (P2 #7).
  // Strictly 2-D (`allowZ:false`): an `x,y,z` token REJECTS like the legacy
  // `parseCoordinatePairList` arity guard (S15 Slice A write parity).
  pointArray<LwpolylineEntity>("Vertices", "vertices", (e) => e.vertices, {
    allowZ: false,
  }),
  // production emits Closed as "true"/"false" (adapters compare === "true"); a
  // plain Yes/No boolean re-opened closed polylines (P2 #11). The WRITE accepts
  // the legacy translator's full truthy-token set (yes/true/closed/1 — S15
  // Slice A parity with the deleted LWPOLYLINE branch), everything else → open.
  make(
    "Closed",
    "boolean",
    { path: "closed", read: (e) => (e as LwpolylineEntity).closed },
    {
      display: (e) => ((e as LwpolylineEntity).closed ? "true" : "false"),
      toRaw: (s) =>
        ["yes", "true", "closed", "1"].includes(s.trim().toLowerCase()),
    },
  ),
  // DXF group 70 bit 128 — plinegen (continuous linetype generation), MATCHPROP-
  // special copyable. S15 Slice A review round: the single-select bag
  // (`buildLwpolylinePreview`) now ALWAYS emits the key in the canonical
  // "true"/"false" form (same convention as Closed — required for the
  // properties/apply undo inverse + the live MATCHPROP plinegen copy), so the
  // descriptor display mirrors that production form EXACTLY (not the default
  // Yes/No boolean projection). The WRITE keeps the default truthy-token parse
  // (yes/true/on/1 → true, anything else → false).
  make(
    "Linetype generation",
    "boolean",
    { path: "plinegen", read: (e) => (e as LwpolylineEntity).plinegen ?? false },
    {
      display: (e) => ((e as LwpolylineEntity).plinegen ? "true" : "false"),
    },
  ),
  num<LwpolylineEntity>("Constant width", "constantWidth", (e) => e.constantWidth, { min: 0 }),
  num<LwpolylineEntity>("Elevation", "elevation", (e) => e.elevation),
  calc<LwpolylineEntity>("Length", (e) => computePolylineLength(e.vertices, e.closed)),
  calc<LwpolylineEntity>("Area", (e) => computePolylineArea(e.vertices, e.closed)),
  // production Bulges = per-vertex bulge list `0.2;0` (formatNumber-joined), NOT
  // array stringification (P2 #3). read=raw bulge array; toRaw is STRICT +
  // COUNT-LOCKED (P2 R4 #3): a non-finite token OR a length ≠ vertex count
  // rejects the whole edit (returns null → S15 no-op) so an edited cell can
  // never desync bulges from vertices / NaN-corrupt an arc segment. Splits on
  // `;` OR `,` — the deleted legacy `parseBulgeListStrict` accepted both
  // separators (`/[;,]/`), so the typed write does too (S15 Slice A parity).
  make(
    "Bulges",
    "text",
    {
      path: "vertices[].bulge",
      read: (e) => (e as LwpolylineEntity).vertices.map((v) => v.bulge ?? 0),
    },
    {
      display: (e) =>
        (e as LwpolylineEntity).vertices.map((v) => formatNumber(v.bulge ?? 0)).join(";"),
      toRaw: (s, entity) => {
        const vertexCount = (entity as LwpolylineEntity | undefined)?.vertices.length;
        const tokens = s.trim() === "" ? [] : s.split(/[;,]/);
        if (tokens.some((t) => t.trim() === "")) return null; // empty token reject (P2 R5 #1)
        const nums = tokens.map((t) => Number(t));
        if (nums.some((n) => !Number.isFinite(n))) return null;
        if (vertexCount !== undefined && nums.length !== vertexCount) return null;
        return nums;
      },
    },
  ),
];
